import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Avatar, Box, Link, Paper, Typography, Unstable_Grid2 as Grid } from '@mui/material';
import moment from 'moment';
import theme from 'theme';

import {
  DataTable,
  DataTableActions,
  DataTableColumn,
  DataTablePagination,
  SocialIcon,
} from 'components';
import { EngagementHistory, EngagementHistoryFormat } from 'api';
import { getSubdomainUrl } from 'helpers';
import { useProtectedClient } from 'hooks';

import { getEngagementType } from './helpers';
import FollowConnectButton from './FollowConnectButton';
import ActionButton from 'features/network/components/ActionButton';

const styles = {
  avatar: {
    width: 48,
    height: 48,
  },
  bio: {
    ...theme.mixins.clampInline(2),
  },
  name: {
    padding: 2,
    paddingLeft: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  actions: {
    marginRight: 3,
  },
  actionButton: {
    color: 'primary.secondary',
  },
} as const;

interface Props extends DataTablePagination {
  format: EngagementHistoryFormat;
  results: EngagementHistory[];
}

const NetworkHistoryTable: FC<Props> = ({
  format,
  results,
  isExhausted,
  sentinelRef,
  isInitialLoad,
}) => {
  const { t } = useTranslation();
  const client = useProtectedClient();

  const columns: DataTableColumn<EngagementHistory>[] = [
    {
      width: 72,
      headerCell: {
        render: t('common:name'),
      },
      bodyCell: {
        render: item => (
          <Link href={item.network_profile_url} target="_blank" underline="hover">
            <Avatar
              src={item.network_profile_image}
              alt={item.network_profile_name}
              sx={styles.avatar}
            />
          </Link>
        ),
      },
    },
    {
      width: 200,
      headerCell: {
        render: null,
      },
      bodyCell: {
        padding: 'none',
        render: item => (
          <Box sx={styles.name}>
            <Link
              title={item.network_profile_name}
              variant="body2"
              href={item.network_profile_url}
              target="_blank"
              noWrap
              underline="hover"
            >
              {item.network_profile_name}
            </Link>
          </Box>
        ),
      },
    },
    {
      width: 72,
      headerCell: {
        render: t('common:network'),
      },
      bodyCell: {
        render: item => (
          <Box display="flex" justifyContent="center">
            <SocialIcon provider={item.network} />
          </Box>
        ),
      },
    },
    {
      width: 550,
      headerCell: {
        render: t('common:bio'),
      },
      bodyCell: {
        render: item => (
          <Typography title={item.network_bio} sx={styles.bio}>
            {item.network_bio}
          </Typography>
        ),
      },
    },
  ];

  if (format === EngagementHistoryFormat.Chronological) {
    columns.push(
      {
        width: 170,
        headerCell: {
          render: t('common:engagement'),
        },
        bodyCell: {
          render: item => (
            <Typography color="primary" title={getEngagementType(item.engagement_type)} noWrap>
              {getEngagementType(item.engagement_type)}
            </Typography>
          ),
        },
      },
      {
        width: 200,
        headerCell: {
          render: t('common:date'),
        },
        bodyCell: {
          render: item => (
            <Typography noWrap>{moment(item.timestamp, 'X').format('MM/DD/YY')}</Typography>
          ),
        },
      }
    );
  }

  if (format === EngagementHistoryFormat.Aggregate) {
    columns.push({
      width: 100,
      headerCell: {
        render: t('common:engagement_plural'),
      },
      bodyCell: {
        render: item => <Typography color="primary">{item.total_engagements}</Typography>,
      },
    });
  }

  const actions: DataTableActions<EngagementHistory> = item => {
    if (format === EngagementHistoryFormat.Aggregate) {
      return (
        <Grid container alignItems="center" spacing={2} sx={styles.actions}>
          <Grid>
            <FollowConnectButton item={item} />
          </Grid>
        </Grid>
      );
    }

    const contentUrl = `${getSubdomainUrl(client.subdomain)}/content/${item.content_id}`;

    return (
      <Grid container alignItems="center" spacing={2} sx={styles.actions}>
        {!!item.content_id && (
          <Grid>
            <ActionButton href={contentUrl} text={t('network:viewPost')} sx={styles.actionButton} />
          </Grid>
        )}
        {!!item.share_url && (
          <Grid>
            <ActionButton
              href={item.share_url}
              text={t('network:viewShare')}
              sx={styles.actionButton}
            />
          </Grid>
        )}
        <Grid>
          <FollowConnectButton item={item} />
        </Grid>
      </Grid>
    );
  };

  return (
    <Paper>
      <DataTable
        data={results}
        columns={columns}
        pagination={{
          isInitialLoad,
          isExhausted,
          sentinelRef,
        }}
        rowSelection={null}
        actions={actions}
      />
    </Paper>
  );
};

export default NetworkHistoryTable;
